import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid } from '@material-ui/core';
import Form from '../form/components/Form';
import SubmitButton from '../form/components/SubmitButton';
import { IdPropType, RefPropType } from '../../proptypes/basic';
import TextInput from '../form/components/TextInput';
import { selectExtraCostById, storeExtraCost, updateExtraCost } from './ExtraCostsSlice';
import { EXTRA_COST_RESOURCE } from '../api/resources';
import { useInitialValues } from '../form/hooks';
import { extraCostsSchema_OtherLocations, extraCostsSchema_HeadOfficeLocation  } from './schema';
import EmployeeSelect from '../form/components/specialized/EmployeeSelect';
import AgencySelect from '../form/components/specialized/AgencySelect';
import DateInput from '../form/components/DateInput';
import { useCustomer } from '../customers/customersSlice';
import { useCanWrite } from '../abilities/hooks';
import { getHeadquarterLocationId } from '../../helpers/auth';

const ExtraCostsForm = ({ customerId, extraCostId, onDone, submitRef }) => {
    const dispatch = useDispatch();
    const extraCost = useSelector((state) => selectExtraCostById(state, extraCostId));
    const canWrite = useCanWrite(EXTRA_COST_RESOURCE);

    // get the location of the customer and choose the schema accordingly
    const [customer] = useCustomer(customerId);
    const locationId = customer.locationId
    const schema = locationId === getHeadquarterLocationId() ? extraCostsSchema_HeadOfficeLocation : extraCostsSchema_OtherLocations;

    const initialValues = useInitialValues(extraCost, {
        customerId: customerId,
        employeeId: '',
        agencyId: '',
        fullAmount: '',
        shareLocation: '',
        note: '',
        billedAt: '',
   });

    const handleSubmit = async (values) => {
            const prepped = {
                id: extraCostId,
                customerId,
                ...values,
            };

            return Promise.all([
                dispatch(extraCostId ? updateExtraCost(prepped) : storeExtraCost(prepped)),
            ]).then(([_extraCost]) => (onDone ? onDone(_extraCost) : null));
        };

    return (
        <Form
            initialValues={initialValues}
            validationSchema={schema}
            onSubmit={handleSubmit}
            subject={extraCost || EXTRA_COST_RESOURCE}
        >
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                                <AgencySelect 
                                    name="agencyId"
                                    label="Agentur"
                                    fullWidth
                                    disabled={!canWrite}
                                />
                        </Grid>
                        <Grid item xs={12}>
                            <DateInput name="billedAt"
                                       label="Datum"
                                       fullWidth
                                        disabled={!canWrite}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextInput 
                                name="fullAmount"
                                label="Betrag gesamt"
                                fullWidth
                                disabled={!canWrite}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextInput 
                                name="shareLocation"
                                label="Anteil Standort"
                                fullWidth
                                disabled={!canWrite}
                            />
                        </Grid>
                        <Grid item xs={12}>
                           <TextInput
                                label="Kommentar"
                                name="note"
                                multiline
                                rows={11}
                                fullWidth
                                disabled={!canWrite}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <EmployeeSelect
                                    label="Kundenbetreuer"
                                    name="employeeId"
                                    fullWidth
                                    userLocation
                                    disabled={!canWrite}
                                />
                        </Grid>
                    </Grid>
                </Grid>
                {submitRef ? (
                    <SubmitButton container={submitRef} disabled={!canWrite} />
                ) : (
                    <Grid item xs={12}>
                        <Box textAlign="right">
                            <SubmitButton />
                        </Box>
                    </Grid>
                )}
            </Grid>
        </Form>
    );
};

ExtraCostsForm.propTypes = {
    customerId: IdPropType.isRequired,
    extraCostId: IdPropType,
    onDone: PropTypes.func,
    submitRef: RefPropType,
};

ExtraCostsForm.defaultProps = {
    extraCostId: null,
    onDone: () => null,
    submitRef: null,
};

export default ExtraCostsForm;